import { cn } from '@recipes/ui';
import {
  BookOpen,
  CalendarDays,
  Carrot,
  Home,
  CircleHelp,
  LogOut,
  MapPin,
  Plus,
  ShoppingCart,
  User,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useAuth } from '@/lib/auth/auth-provider';
import { ServiceWorkerPrompt } from '@/components/pwa/service-worker-prompt';
import { AiChatDock } from '@/features/ai-chat/chat-dock';
import { useCatalogBoot } from '@/features/catalog/hooks';
import { AppBackdrop } from '@/features/appearance/backdrop';
import { ColorModeToggle } from '@/features/appearance/color-mode-toggle';
import { useAppearanceSync } from '@/features/appearance/use-appearance';
import { LanguageSwitcher, useLanguageSync } from '@/features/language/language-switcher';
import { useFeature } from '@/features/shared/hooks';
import { useGuardedLogout, useShoppingBoot } from '@/features/shopping/hooks';

function useOnline(): boolean {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

// The order is the user's (2026-09-25): start, shopping, recipes, plan, restaurants, catalogs,
// add, profile.
const NAV = [
  { to: '/', icon: Home, key: 'nav.home', end: true },
  { to: '/shopping', icon: ShoppingCart, key: 'nav.shopping' },
  { to: '/recipes', icon: BookOpen, key: 'nav.recipes' },
  { to: '/meal-plan', icon: CalendarDays, key: 'nav.mealPlan' },
  // Restaurants: in the sidebar only – the bottom bar of a phone is full with seven entries;
  // there the way in is the start page and "Add".
  {
    to: '/tasting',
    icon: MapPin,
    key: 'nav.tasting',
    feature: 'tasting_notes',
    sidebarOnly: true,
  },
  // Catalogs: ingredients, products, stores and loyalty cards; only with the catalog switched on.
  { to: '/catalog', icon: Carrot, key: 'nav.catalog', feature: 'ingredient_catalog' },
  { to: '/add', icon: Plus, key: 'nav.add', primary: true },
  { to: '/profile', icon: User, key: 'nav.profile' },
] as const;

/**
 * Responsive shell: bottom tab bar on phones, left sidebar on desktop. Cooking mode
 * (/cook/*) renders full-screen without chrome.
 */
export function AppShell() {
  const { t } = useTranslation();
  const { status } = useAuth();
  const logout = useGuardedLogout();
  const location = useLocation();
  const online = useOnline();
  const fullscreen = location.pathname.startsWith('/cook/');
  const showNav = status === 'authenticated' && !fullscreen;
  const catalogEnabled = useFeature('ingredient_catalog');
  const tastingEnabled = useFeature('tasting_notes');
  const sidebarNav = NAV.filter(
    (item) =>
      !('feature' in item) ||
      (item.feature === 'ingredient_catalog' ? catalogEnabled : tastingEnabled),
  );
  const nav = sidebarNav.filter((item) => !('sidebarOnly' in item));
  // The catalog's device copy is synchronized from every screen, not only when a list opens.
  useCatalogBoot();
  // Same for the shopping lists: they are on the device before the user is in the store.
  useShoppingBoot();
  // The profile's language follows the user to this device – and a choice made here to the profile.
  useLanguageSync();
  // …and so does the look: theme, light or dark, background.
  useAppearanceSync();

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      <AppBackdrop />
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-background focus:px-3 focus:py-2"
      >
        {t('app.skipToContent')}
      </a>
      {/* Language and mode: before sign-in a button in the top right corner; signed in they
          live in the sidebar on desktop and in the profile on a phone (user, 2026-09-25: no
          controls in the corner of the UI). Cooking mode belongs to the recipe – no button there. */}
      {!fullscreen && !showNav && (
        <div className="fixed right-2 top-[max(0.5rem,env(safe-area-inset-top))] z-40 flex items-center gap-1">
          {/* Light / dark / system next to the language – also before sign-in. */}
          <ColorModeToggle variant="icon" />
          <LanguageSwitcher variant={showNav ? 'icon' : 'labelled'} />
        </div>
      )}
      {showNav && (
        <nav
          aria-label={t('nav.primary')}
          className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col overflow-y-auto border-r bg-card p-4 md:flex"
        >
          <div className="mb-6 text-lg font-bold">{t('app.name')}</div>
          <ul className="space-y-1">
            {sidebarNav.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={'end' in item ? item.end : false}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium hover:bg-accent',
                      isActive && 'bg-accent text-accent-foreground',
                    )
                  }
                >
                  <item.icon className="size-5" aria-hidden />
                  {t(item.key)}
                </NavLink>
              </li>
            ))}
          </ul>
          <NavLink
            to="/help"
            className={({ isActive }) =>
              cn(
                'mt-auto flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                isActive && 'bg-accent text-accent-foreground',
              )
            }
            data-testid="sidebar-help"
          >
            <CircleHelp className="size-5" aria-hidden />
            {t('nav.help')}
          </NavLink>
          <ColorModeToggle variant="labelled" className="justify-start gap-3" />
          <LanguageSwitcher variant="labelled" className="justify-start gap-3" />
          <button
            type="button"
            onClick={() => void logout()}
            className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            data-testid="sidebar-logout"
          >
            <LogOut className="size-5" aria-hidden />
            {t('nav.logout')}
          </button>
        </nav>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        {!online && (
          <div role="status" className="bg-warning/20 px-4 py-1 text-center text-xs">
            {t('app.offline')}
          </div>
        )}
        <main id="main" className={cn('flex-1', showNav && 'pb-20 md:pb-0')}>
          <Outlet />
        </main>
        <ServiceWorkerPrompt />
      </div>
      <AiChatDock />
      {showNav && (
        <nav
          aria-label={t('nav.primary')}
          className="fixed inset-x-0 bottom-0 z-30 border-t bg-card safe-bottom md:hidden"
        >
          <ul
            className="grid"
            style={{ gridTemplateColumns: `repeat(${nav.length}, minmax(0, 1fr))` }}
          >
            {nav.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={'end' in item ? item.end : false}
                  className={({ isActive }) =>
                    cn(
                      'flex min-h-[56px] flex-col items-center justify-center gap-0.5 font-medium',
                      // Seven entries have to fit on a 360 px wide phone.
                      nav.length > 6 ? 'text-[10px]' : 'text-[11px]',
                      isActive ? 'text-primary' : 'text-muted-foreground',
                    )
                  }
                >
                  <item.icon
                    className={cn(
                      'size-6',
                      'primary' in item &&
                        'rounded-full bg-primary p-1 text-primary-foreground size-8',
                    )}
                    aria-hidden
                  />
                  {/* With seven entries the long labels do not fit; the link keeps its full name. */}
                  <span className="max-w-full truncate px-0.5" aria-hidden={nav.length > 6}>
                    {nav.length > 6
                      ? t(`${item.key}Short`, { defaultValue: t(item.key) })
                      : t(item.key)}
                  </span>
                  {nav.length > 6 && <span className="sr-only">{t(item.key)}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}
